import { X } from "@phosphor-icons/react";
import { Button, Dialog, Flex } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  TRY_WALKTHROUGH_DIALOG_MIN_H_CLASS,
  TRY_WALKTHROUGH_PREVIEW_FRAME_CLASS,
  type TryFeatureConfig,
  type TryIntroStep,
} from "./tryFeatures";
import {
  TryWalkthroughCompose,
  type TryWalkthroughComposeValue,
} from "./TryWalkthroughCompose";
import { TryWalkthroughUpload } from "./TryWalkthroughUpload";

import { useStartNewProjectChat } from "../../../../utils/useStartNewProjectChat";
import { type AttachmentItem } from "../../hooks/useAttachedFiles";
import { type HomeComposerMode } from "../HomeComposerModeToggle";

export type TryLaunchRequest = {
  source: "skip" | "getStarted";
  compose?: {
    mode: HomeComposerMode;
    projectId: string | null;
    sharedChatId?: string | null;
    message: string;
  };
};

type TryWalkthroughDialogProps = {
  open: boolean;
  feature: TryFeatureConfig;
  onLaunch: (request?: TryLaunchRequest) => void;
  onDismiss: () => void;
  onFilesSelected?: (files: File[]) => void;
  attachedItems?: AttachmentItem[];
  onRemoveFile?: (index: number) => void;
  hasPendingUploads?: boolean;
  /** True once at least one walkthrough file is ready to send on createChat. */
  hasReadyAttachments?: boolean;
  isLaunching?: boolean;
};

const EMPTY_COMPOSE: TryWalkthroughComposeValue = {
  mode: "chat",
  projectId: null,
  projectName: null,
  message: "",
};

function WalkthroughCloseButton({
  disabled,
  label,
}: {
  disabled: boolean;
  label: string;
}) {
  return (
    <Dialog.Close>
      <button
        type="button"
        data-testid="ask-puntt-try-walkthrough-close"
        aria-label={label}
        disabled={disabled}
        className="bg-white/90 hover:bg-white flex size-6 items-center justify-center rounded-md text-puntt-neutral-gray-11 shadow-sm hover:text-puntt-neutral-gray-12 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <X size={16} weight="bold" aria-hidden="true" />
      </button>
    </Dialog.Close>
  );
}

function PreviewImage({
  featureId,
  step,
}: {
  featureId: string;
  step: TryIntroStep & { previewSrc: string };
}) {
  const { t } = useTranslation("ui");
  return (
    <img
      src={step.previewSrc}
      alt={t(`tryWalkthrough.${featureId}.steps.${step.stepKey}.previewAlt`)}
      data-testid="ask-puntt-try-walkthrough-preview"
      className="block size-full object-cover object-top"
    />
  );
}

function ProgressDots({
  stepIndex,
  total,
}: {
  stepIndex: number;
  total: number;
}) {
  const { t } = useTranslation("ui");
  return (
    <div
      className="flex items-center gap-1.5"
      data-testid="ask-puntt-try-walkthrough-progress"
      aria-label={t("tryWalkthrough.progressAriaLabel", {
        current: stepIndex + 1,
        total,
      })}
      role="status"
    >
      {Array.from({ length: total }, (_, i) =>
        i === stepIndex ? (
          <span
            key={i}
            className="h-1.5 w-4 rounded-full bg-puntt-accent-9"
            aria-hidden="true"
          />
        ) : (
          <span
            key={i}
            className="size-1.5 rounded-full bg-puntt-neutral-gray-6"
            aria-hidden="true"
          />
        ),
      )}
    </div>
  );
}

function composeHasPayload(
  compose: TryWalkthroughComposeValue,
  hasReadyAttachments: boolean,
): boolean {
  return compose.message.trim().length > 0 || hasReadyAttachments;
}

/** Empty compose is allowed (Try it is optional). A typed Project message
 * still needs a selected project so Get started does not silently no-op. */
function composeCanComplete(
  compose: TryWalkthroughComposeValue,
  hasReadyAttachments: boolean,
): boolean {
  if (
    compose.mode === "project" &&
    composeHasPayload(compose, hasReadyAttachments) &&
    compose.projectId == null
  ) {
    return false;
  }
  return true;
}

/**
 * Stepped walkthrough modal for a Try feature. Skip reports `source: "skip"`
 * with no payload (home Chat/Project consumes Try and stays on home; skill
 * cards still launch). Get started on an upload last step requires a ready
 * attachment; on a compose last step the compose payload is optional (empty
 * Get started is the same dismiss-only path as Skip). A typed message in
 * Project mode still requires a selected project. X / overlay dismiss closes
 * without consuming Try. The dialog stays open until launch succeeds so a
 * failed create does not consume Try.
 */
export function TryWalkthroughDialog({
  open,
  feature,
  onLaunch,
  onDismiss,
  onFilesSelected,
  attachedItems = [],
  onRemoveFile,
  hasPendingUploads = false,
  hasReadyAttachments = false,
  isLaunching = false,
}: TryWalkthroughDialogProps) {
  const { t } = useTranslation("ui");
  const [stepIndex, setStepIndex] = useState(0);
  const [compose, setCompose] =
    useState<TryWalkthroughComposeValue>(EMPTY_COMPOSE);
  const { startNewProjectChat } = useStartNewProjectChat(
    "ask_puntt_try_walkthrough",
    (project) => {
      setCompose((current) => ({
        ...current,
        mode: "project",
        projectId: project.id,
        projectName: project.name,
        sharedChatId: project.sharedChatId,
      }));
    },
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setStepIndex(0);
    setCompose(EMPTY_COMPOSE);
  }, [open]);

  const steps = feature.steps;
  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;
  const isFirst = stepIndex === 0;
  const launchBlocked = hasPendingUploads || isLaunching;
  const uploadGetStartedBlocked =
    step.kind === "upload" && (hasPendingUploads || !hasReadyAttachments);
  const composeGetStartedBlocked =
    step.kind === "compose" &&
    (hasPendingUploads || !composeCanComplete(compose, hasReadyAttachments));
  const getStartedBlocked =
    isLaunching ||
    (isLast && uploadGetStartedBlocked) ||
    (isLast && composeGetStartedBlocked);
  const base = `tryWalkthrough.${feature.featureId}`;
  const closeLabel = t("tryWalkthrough.closeAriaLabel");
  const introPreviewSrc =
    step.kind === "intro" && step.previewSrc != null && step.previewSrc !== ""
      ? step.previewSrc
      : undefined;

  if (!step) return null;

  const handleGetStarted = () => {
    if (step.kind === "compose") {
      const hasPayload = composeHasPayload(compose, hasReadyAttachments);
      const hasProject =
        compose.mode === "project" && compose.projectId != null;
      if (!hasPayload && !hasProject) {
        onLaunch({ source: "getStarted" });
        return;
      }
      if (compose.mode === "project" && compose.projectId == null) {
        return;
      }
      onLaunch({
        source: "getStarted",
        compose: {
          mode: compose.mode,
          projectId: compose.projectId,
          sharedChatId: compose.sharedChatId ?? null,
          message: compose.message,
        },
      });
      return;
    }
    onLaunch({ source: "getStarted" });
  };

  return (
    <>
      <Dialog.Root
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isLaunching) onDismiss();
        }}
      >
        <Dialog.Content
          maxWidth="520px"
          data-testid="ask-puntt-try-walkthrough"
          className={`relative flex ${TRY_WALKTHROUGH_DIALOG_MIN_H_CLASS} flex-col overflow-hidden p-0`}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          {step.kind === "intro" ? (
            <div
              className={`relative overflow-hidden bg-puntt-neutral-gray-2 ${TRY_WALKTHROUGH_PREVIEW_FRAME_CLASS}`}
              data-testid="ask-puntt-try-walkthrough-preview-frame"
            >
              {introPreviewSrc != null ? (
                <PreviewImage
                  featureId={feature.featureId}
                  step={{ ...step, previewSrc: introPreviewSrc }}
                />
              ) : (
                <div
                  role="img"
                  aria-label={t(`${base}.steps.${step.stepKey}.previewAlt`)}
                  data-testid="ask-puntt-try-walkthrough-preview-placeholder"
                  className="size-full"
                />
              )}
              <div className="absolute right-3 top-3 z-10">
                <WalkthroughCloseButton
                  disabled={isLaunching}
                  label={closeLabel}
                />
              </div>
            </div>
          ) : (
            <div className="flex h-10 items-center justify-end px-3 pt-2">
              <WalkthroughCloseButton
                disabled={isLaunching}
                label={closeLabel}
              />
            </div>
          )}

          <div className="flex flex-1 flex-col px-8 py-5">
            <Dialog.Title size="5" mb="2" className="leading-snug">
              {t(`${base}.steps.${step.stepKey}.title`)}
            </Dialog.Title>
            <Dialog.Description
              size="2"
              mb="0"
              className="text-puntt-neutral-gray-11"
            >
              {t(`${base}.steps.${step.stepKey}.body`)}
            </Dialog.Description>

            {step.kind === "upload" && (
              <TryWalkthroughUpload
                onFilesSelected={onFilesSelected ?? (() => undefined)}
                attachedItems={attachedItems}
                onRemoveFile={onRemoveFile ?? (() => undefined)}
              />
            )}

            {step.kind === "compose" && (
              <TryWalkthroughCompose
                data-testid="ask-puntt-try-walkthrough-compose"
                value={compose}
                onChange={setCompose}
                onNewProject={() => {
                  void startNewProjectChat("try_walkthrough_compose");
                }}
                disabled={isLaunching}
                attachedItems={attachedItems}
                onFilesSelected={onFilesSelected ?? (() => undefined)}
                onRemoveFile={onRemoveFile ?? (() => undefined)}
              />
            )}
          </div>

          <Flex
            align="center"
            justify="between"
            className="border-t border-puntt-neutral-gray-6 px-6 py-3"
          >
            <Flex align="center" gap="3">
              {!isFirst && (
                <Button
                  type="button"
                  variant="ghost"
                  color="gray"
                  size="2"
                  data-testid="ask-puntt-try-walkthrough-back"
                  onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                >
                  {t("tryWalkthrough.back")}
                </Button>
              )}
              <button
                type="button"
                data-testid="ask-puntt-try-walkthrough-skip"
                disabled={launchBlocked}
                className="cursor-pointer bg-transparent p-0 text-sm text-puntt-neutral-gray-11 underline-offset-2 hover:text-puntt-neutral-gray-12 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:no-underline"
                onClick={() => onLaunch({ source: "skip" })}
              >
                {t("tryWalkthrough.skip")}
              </button>
            </Flex>

            <ProgressDots stepIndex={stepIndex} total={steps.length} />

            <Button
              type="button"
              size="2"
              variant="solid"
              data-testid="ask-puntt-try-walkthrough-next"
              disabled={isLast ? getStartedBlocked : isLaunching}
              onClick={() => {
                if (isLast) handleGetStarted();
                else setStepIndex((i) => Math.min(steps.length - 1, i + 1));
              }}
            >
              {isLast
                ? t("tryWalkthrough.getStarted")
                : t("tryWalkthrough.next")}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
